import { AlertTriangle, ChevronRight, FileText, Loader2, Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Markdown } from "@/components/shared/Markdown";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { sowiftService } from "@/lib/services";
import type { SowiftDocumentResponse, SowiftProjectResponse } from "@/types";
import { SOWEditor } from "./SOWEditor";

export function DocumentsTab({
  projectId,
  project,
  onProjectUpdate,
}: Readonly<{
  projectId: string;
  project: SowiftProjectResponse;
  onProjectUpdate: () => Promise<void>;
}>) {
  const [activeDoc, setActiveDoc] = useState<SowiftDocumentResponse | null>(null);
  const [generating, setGenerating] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<SowiftDocumentResponse[]>([]);

  // Operation stream for SOW generation
  const [generateOperationId, setGenerateOperationId] = useState<string | null>(null);
  const fullContentRef = useRef("");
  const newDocUuidRef = useRef<string | null>(null);

  const loadDocuments = async () => {
    try {
      const response = await sowiftService.listDocuments(projectId);
      setDocuments(response.data);
      // Auto-select the latest document
      if (response.data.length > 0 && !activeDoc) {
        setActiveDoc(response.data[0]);
      }
    } catch (err) {
      console.error("Failed to load documents:", err);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [projectId]);

  const handleGenerateEvent = useCallback(
    (event: OperationEvent) => {
      const { event: eventName, data } = event;

      switch (eventName) {
        case "content_delta": {
          const delta = (data as { delta: string }).delta;
          fullContentRef.current += delta;
          setStreamingContent(fullContentRef.current);
          break;
        }
        case "generation_end": {
          newDocUuidRef.current = (data as { documentUuid: string }).documentUuid;
          break;
        }
        case "operation_completed": {
          // Reload documents and select the new one
          const finalize = async () => {
            await loadDocuments();
            await onProjectUpdate();
            if (newDocUuidRef.current) {
              try {
                const docResponse = await sowiftService.getDocument(projectId, newDocUuidRef.current);
                setActiveDoc(docResponse.data);
              } catch (e) {
                console.error("Failed to load new document:", e);
              }
            }
            setStreamingContent("");
            setAdditionalInstructions("");
            setGenerating(false);
            setGenerateOperationId(null);
            fullContentRef.current = "";
            newDocUuidRef.current = null;
          };
          finalize();
          break;
        }
        case "operation_failed": {
          const errorData = data as { error?: string };
          setError(errorData.error || "Failed to generate SOW");
          setGenerating(false);
          setGenerateOperationId(null);
          fullContentRef.current = "";
          newDocUuidRef.current = null;
          break;
        }
        case "error": {
          const errorMsg = (data as { message: string }).message;
          setError(errorMsg);
          break;
        }
      }
    },
    [projectId, onProjectUpdate],
  );

  useOperationStream(generateOperationId, {
    onEvent: handleGenerateEvent,
    onError: (err) => {
      setError(err);
      setGenerating(false);
      setGenerateOperationId(null);
      fullContentRef.current = "";
      newDocUuidRef.current = null;
    },
  });

  const handleGenerate = async () => {
    if (project.contextItems.length === 0) {
      setError("Add context items before generating a SOW.");
      return;
    }

    try {
      setError(null);
      setGenerating(true);
      setStreamingContent("");
      setShowGenerateModal(false);
      setActiveDoc(null);
      fullContentRef.current = "";
      newDocUuidRef.current = null;

      const result = await sowiftService.generateSOW(projectId, {
        additionalInstructions: additionalInstructions.trim() || undefined,
      });
      setGenerateOperationId(result.operation_id);
    } catch (err) {
      console.error("Failed to generate SOW:", err);
      setError("Failed to generate SOW. Please try again.");
      setGenerating(false);
    }
  };

  // While generating, show the streaming preview
  if (generating) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-brand-primary animate-spin" />
          <h3 className="text-sm font-medium text-content-primary">Generating Statement of Work...</h3>
        </div>
        <div className="bg-surface-primary border border-border-primary rounded-xl p-6 min-h-[400px]">
          <Markdown content={streamingContent || "*Generating...*"} isStreaming />
        </div>
      </div>
    );
  }

  // If we have an active document, show the editor
  if (activeDoc) {
    return (
      <SOWEditor
        projectId={projectId}
        document={activeDoc}
        documents={documents}
        onDocumentSelect={(doc) => setActiveDoc(doc)}
        onBack={() => setActiveDoc(null)}
        onGenerate={() => setShowGenerateModal(true)}
        onDocumentUpdate={(updated) => {
          setActiveDoc(updated);
          setDocuments((prev) => prev.map((d) => (d.uuid === updated.uuid ? updated : d)));
        }}
      />
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Generate Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-content-tertiary">Documents ({documents.length})</h3>
        <button
          onClick={() => setShowGenerateModal(true)}
          disabled={project.contextItems.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Sparkles className="h-4 w-4" />
          Generate SOW
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Document List */}
      {documents.length > 0 ? (
        <div className="space-y-3">
          {documents.map((doc) => (
            <button
              key={doc.uuid}
              onClick={() => setActiveDoc(doc)}
              className="w-full bg-surface-primary border border-border-primary hover:border-border-secondary rounded-xl p-4 text-left transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-content-primary">{doc.title}</h4>
                  <div className="flex items-center gap-3 mt-1 text-xs text-content-quaternary">
                    <span>v{doc.version}</span>
                    <span className={`capitalize ${doc.status === "final" ? "text-green-400" : ""}`}>{doc.status}</span>
                    <span>{doc.documentType.toUpperCase()}</span>
                    <span>{new Date(doc.updatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-content-quaternary" />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FileText className="h-10 w-10 text-content-quaternary mx-auto mb-3" />
          <p className="text-content-tertiary text-sm">
            No documents generated yet. Add context and run an analysis, then generate your Statement of Work.
          </p>
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50">
          <div className="bg-surface-primary border border-border-secondary rounded-xl p-6 max-w-lg mx-4 w-full">
            <h3 className="text-lg font-medium text-content-primary flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand-primary" />
              Generate Statement of Work
            </h3>
            <p className="text-content-tertiary mt-2 text-sm">
              AI will analyze your context items and any analysis sessions to generate a comprehensive SOW.
              {documents.length > 0 && (
                <span className="block mt-1 text-brand-primary">This will create version {documents.length + 1}.</span>
              )}
            </p>
            <div className="mt-4">
              <label htmlFor="additional-instructions" className="block text-xs text-content-quaternary mb-1">
                Additional Instructions (optional)
              </label>
              <textarea
                id="additional-instructions"
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
                placeholder="e.g. Focus on the technical implementation details, include specific milestones for Q1..."
                rows={3}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary text-sm focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none"
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm font-medium text-content-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <Sparkles className="h-4 w-4" />
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
